import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { DownloadSimple, MagnifyingGlass, Package, Tag, Truck } from '@phosphor-icons/react'
import { Badge, Banner, Button, DataTable, EmptyState, FilterChip, Input, Select, type Column } from '@/ui'
import { isApiError } from '@/api'
import { istDateKey } from '@/lib/ist'
import { errorMessage } from '../api/core'
import { ck } from '../api/keys'
import { useCrops, useUser } from '../api/reference'
import {
  stockLookup,
  stockStatus,
  useDock,
  useRedTie,
  useSites,
  useStock,
  type DockRow,
  type LotLookup,
  type RedTieRow,
  type StockRow,
} from '../api/store'
import { BoolFilter, FilterBar, SearchFilter, SelectFilter } from '../components/FilterBar'
import { ConsolePage } from '../components/Layout'
import { PersonPicker } from '../components/PersonPicker'
import { Code, Kg, RefLink, StatTile, Status, Stream, When } from '../components/bits'
import { downloadText, toCsv } from '../lib/csv'
import { useUrlState } from '../lib/useUrlState'
import { STOCK_CSV_HEADER, groupStockByLot, isDockStale, placeLabel, stockCsvRows, stockTotals, type LotStock } from '../model/stock'
import { humanize } from '../labels'

const LIMIT = 50
type View = 'place' | 'lot' | 'redtie' | 'dock'
const VIEWS: Array<{ id: View; label: string }> = [
  { id: 'place', label: 'By place' },
  { id: 'lot', label: 'By lot' },
  { id: 'redtie', label: 'Red-tie list' },
  { id: 'dock', label: 'Dock' },
]

/** Stock on hand — F5.6: what we hold, where, how long; red ties still to put on; dock waiting. */
export function StockPage() {
  const [f, setF, { reset, active }] = useUrlState({
    view: 'place',
    siteId: '',
    cropId: '',
    stream: '',
    hold: '',
    available: '',
    placeKind: '',
    farmerId: '',
    search: '',
    page: 1,
  })
  const view = (VIEWS.some((v) => v.id === f.view) ? f.view : 'place') as View
  const sites = useSites()
  const siteOptions = (sites.data ?? []).map((s) => ({ value: s.id, label: s.name || s.code }))

  return (
    <ConsolePage
      eyebrow="Store"
      title="Stock on hand"
      description="What we hold, worked out from scans and weights: every lot by place or trip, how long it has been in, and what still needs doing."
    >
      <LotFinder />
      <div className="flex flex-wrap gap-2" role="group" aria-label="View">
        {VIEWS.map((v) => (
          <FilterChip key={v.id} active={view === v.id} onClick={() => setF({ view: v.id, page: 1 })}>
            {v.label}
          </FilterChip>
        ))}
      </div>
      {view === 'place' || view === 'lot' ? (
        <StockList view={view} f={f} setF={setF} reset={reset} active={active} siteOptions={siteOptions} />
      ) : view === 'redtie' ? (
        <RedTieList siteId={f.siteId} onSite={(siteId) => setF({ siteId })} siteOptions={siteOptions} />
      ) : (
        <DockList siteId={f.siteId} onSite={(siteId) => setF({ siteId })} siteOptions={siteOptions} />
      )}
    </ConsolePage>
  )
}

type Filters = {
  view: string
  siteId: string
  cropId: string
  stream: string
  hold: string
  available: string
  placeKind: string
  farmerId: string
  search: string
  page: number
}

function StockList({
  view,
  f,
  setF,
  reset,
  active,
  siteOptions,
}: {
  view: 'place' | 'lot'
  f: Filters
  setF: (p: Partial<Filters>) => void
  reset: () => void
  active: boolean
  siteOptions: Array<{ value: string; label: string }>
}) {
  const crops = useCrops()
  const farmer = useUser(f.farmerId || null)
  const tri = (v: string) => (v === '' ? undefined : v === 'true')
  const q = useStock({
    page: f.page,
    limit: LIMIT,
    siteId: f.siteId || undefined,
    cropId: f.cropId || undefined,
    stream: f.stream || undefined,
    hold: tri(f.hold),
    available: tri(f.available),
    placeKind: f.placeKind || undefined,
    farmerId: f.farmerId || undefined,
    search: f.search || undefined,
  })
  const rows = q.data?.rows ?? []
  const totals = stockTotals(q.data?.summary, rows)
  const scopeHint = totals.scope === 'all' ? 'All matching stock' : 'This page'

  const exportCsv = () =>
    downloadText(`stock-${istDateKey()}.csv`, toCsv(STOCK_CSV_HEADER, stockCsvRows(rows, (r) => stockStatus(r).label)))

  const placeColumns: Column<StockRow>[] = [
    { id: 'lot', header: 'Lot', cell: (r) => <RefLink to={`/c/lots/${r.unitId}`}>{r.unitCode}</RefLink>, sortValue: (r) => r.unitCode },
    { id: 'stream', header: 'Stream', cell: (r) => <Stream stream={r.stream} />, hideBelow: 'lg' },
    {
      id: 'place',
      header: 'Place',
      cell: (r) =>
        r.placeKind === 'TRIP' && r.shipmentId ? (
          <span className="inline-flex items-center gap-1.5">
            <Truck aria-hidden="true" className="text-info" />
            <RefLink to={`/c/shipments/${r.shipmentId}`}>{r.shipmentCode ?? 'Trip'}</RefLink>
          </span>
        ) : (
          <span className="text-[14px]">{placeLabel(r)}</span>
        ),
      sortValue: (r) => placeLabel(r),
    },
    {
      id: 'bags',
      header: 'Bags',
      align: 'right',
      mono: true,
      sortValue: (r) => r.bags,
      cell: (r) => (
        <span>
          {r.bags}
          {r.bagsOnHold > 0 && <span className="ml-1 text-warn">({r.bagsOnHold} on hold)</span>}
        </span>
      ),
    },
    { id: 'kgNow', header: 'Kg now', align: 'right', cell: (r) => <Kg grams={r.kgNowG} />, sortValue: (r) => r.kgNowG },
    { id: 'kgGate', header: 'At gate', align: 'right', cell: (r) => <Kg grams={r.kgAtGateG} />, sortValue: (r) => r.kgAtGateG, hideBelow: 'md' },
    { id: 'days', header: 'Days in', align: 'right', mono: true, cell: (r) => r.daysInStore ?? '—', sortValue: (r) => r.daysInStore, hideBelow: 'sm' },
    {
      id: 'status',
      header: 'Status',
      cell: (r) => {
        const s = stockStatus(r)
        return <Status status={s.status} label={s.label} />
      },
    },
  ]

  const lotColumns: Column<LotStock>[] = [
    { id: 'lot', header: 'Lot', cell: (l) => <RefLink to={`/c/lots/${l.unitId}`}>{l.unitCode}</RefLink>, sortValue: (l) => l.unitCode },
    { id: 'stream', header: 'Stream', cell: (l) => <Stream stream={l.stream} />, hideBelow: 'md' },
    {
      id: 'places',
      header: 'Places',
      cell: (l) => (
        <ul className="flex flex-col gap-0.5">
          {l.places.map((p) => (
            <li key={p.key} className="text-[13.5px]">
              {p.kind === 'TRIP' && p.shipmentId ? (
                <RefLink to={`/c/shipments/${p.shipmentId}`}>{p.label}</RefLink>
              ) : (
                p.label
              )}{' '}
              <span className="font-mono text-[12px] text-muted">· {p.bags} bags</span>
            </li>
          ))}
        </ul>
      ),
    },
    {
      id: 'bags',
      header: 'Bags',
      align: 'right',
      mono: true,
      sortValue: (l) => l.bags,
      cell: (l) => (
        <span>
          {l.bags}
          {l.bagsOnHold > 0 && <span className="ml-1 text-warn">({l.bagsOnHold} on hold)</span>}
        </span>
      ),
    },
    { id: 'kgNow', header: 'Kg now', align: 'right', cell: (l) => <Kg grams={l.kgNowG} />, sortValue: (l) => l.kgNowG },
    { id: 'kgGate', header: 'At gate', align: 'right', cell: (l) => <Kg grams={l.kgAtGateG} />, sortValue: (l) => l.kgAtGateG, hideBelow: 'md' },
    { id: 'days', header: 'Days in', align: 'right', mono: true, cell: (l) => l.daysInStore ?? '—', sortValue: (l) => l.daysInStore, hideBelow: 'sm' },
    {
      id: 'status',
      header: 'Status',
      cell: (l) => {
        const s = stockStatus({ ...l.rows[0]!, bagsOnHold: l.bagsOnHold })
        return <Status status={s.status} label={s.label} />
      },
    },
  ]

  const filters = (
    <FilterBar onClear={reset} active={active && Object.entries(f).some(([k, v]) => k !== 'view' && k !== 'page' && v !== '')}>
      <SearchFilter label="Lot or farmer code" value={f.search} onChange={(search) => setF({ search })} placeholder="e.g. 0087" />
      <SelectFilter label="Site" value={f.siteId} onChange={(siteId) => setF({ siteId })} options={siteOptions} />
      <SelectFilter label="Crop" value={f.cropId} onChange={(cropId) => setF({ cropId })} options={(crops.data ?? []).map((c) => ({ value: c.id, label: c.name }))} />
      <SelectFilter
        label="Stream"
        value={f.stream}
        onChange={(stream) => setF({ stream })}
        options={[
          { value: 'MAIN', label: 'Good' },
          { value: 'CONV', label: 'Ordinary' },
        ]}
      />
      <BoolFilter label="On hold" value={f.hold === '' ? undefined : f.hold === 'true'} onChange={(v) => setF({ hold: v === undefined ? '' : String(v) })} />
      <BoolFilter label="Available" value={f.available === '' ? undefined : f.available === 'true'} onChange={(v) => setF({ available: v === undefined ? '' : String(v) })} />
      <SelectFilter
        label="Where"
        value={f.placeKind}
        onChange={(placeKind) => setF({ placeKind })}
        options={[
          { value: 'LOCATION', label: 'At a place' },
          { value: 'TRIP', label: 'On a trip' },
        ]}
        allLabel="Anywhere"
      />
      <div className="flex min-w-[14rem] flex-col gap-1">
        <span className="font-mono text-[10px] font-bold uppercase tracking-label text-muted">Farmer</span>
        <PersonPicker
          label="Farmer"
          value={f.farmerId ? (farmer.data ?? { id: f.farmerId, name: '…', username: '', farmerCode: null, contactNo: null, roles: [] }) : null}
          onChange={(u) => setF({ farmerId: u?.id ?? '' })}
          placeholder="Name or roster no."
        />
      </div>
    </FilterBar>
  )

  const empty = <EmptyState icon={<Package />} title="No stock matches" description="Lots show here once their bags are scanned into a place or onto a trip." />
  const common = {
    loading: q.isLoading,
    error: q.isError ? errorMessage(q.error) : undefined,
    pageSize: LIMIT,
    server: { page: f.page, total: q.data?.total ?? 0, onPageChange: (page: number) => setF({ page }) },
    filters,
    empty,
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatTile label="Bags" value={totals.bags} hint={scopeHint} loading={q.isLoading} />
        <StatTile label="Kg now" value={<Kg grams={totals.kgNowG} />} hint={scopeHint} loading={q.isLoading} />
        <StatTile label="Kg at the gate" value={<Kg grams={totals.kgAtGateG} />} hint={scopeHint} loading={q.isLoading} />
      </div>
      <div className="-mb-2 flex justify-end">
        <Button size="sm" variant="secondary" onClick={exportCsv} disabled={rows.length === 0}>
          <DownloadSimple aria-hidden="true" /> Export this page
        </Button>
      </div>
      {view === 'place' ? (
        <DataTable<StockRow> caption="Stock by place" columns={placeColumns} rows={rows} getRowId={(r) => r.key} {...common} />
      ) : (
        <DataTable<LotStock> caption="Stock by lot" columns={lotColumns} rows={groupStockByLot(rows)} getRowId={(l) => l.unitId} {...common} />
      )}
    </>
  )
}

/** Find a lot by its code or a bag's tag: where its bags are and which trips they are on. */
function LotFinder() {
  const [mode, setMode] = useState<'code' | 'tag'>('code')
  const [text, setText] = useState('')
  const [asked, setAsked] = useState<{ code?: string; tag?: string } | null>(null)
  const q = useQuery({
    queryKey: [...ck.stock, 'lookup', asked],
    enabled: asked !== null,
    queryFn: () => stockLookup(asked!),
    retry: false,
  })
  const notFound = q.isError && isApiError(q.error) && q.error.status === 404
  return (
    <section className="rounded-card border border-rule bg-surface px-4 py-3" aria-labelledby="find-lot-h">
      <h2 id="find-lot-h" className="mb-2 text-[15px] font-semibold text-ink">
        Find a lot
      </h2>
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          const v = text.trim()
          setAsked(v ? (mode === 'code' ? { code: v } : { tag: v }) : null)
        }}
      >
        <Select<'code' | 'tag'>
          aria-label="Find by"
          value={mode}
          onChange={setMode}
          options={[
            { value: 'code', label: 'Lot code' },
            { value: 'tag', label: 'Bag tag' },
          ]}
          className="h-9 w-36 text-[14px]"
        />
        <Input
          aria-label={mode === 'code' ? 'Lot code' : 'Bag tag'}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={mode === 'code' ? 'HL-2026-1018-A1-0087' : 'Scan or type the tag'}
          maxLength={mode === 'code' ? 24 : 64}
          leading={mode === 'code' ? <MagnifyingGlass /> : <Tag />}
          className="h-9 min-w-[16rem] text-[14px]"
        />
        <Button size="sm" type="submit" loading={q.isFetching}>
          Find
        </Button>
        {asked && (
          <Button size="sm" variant="ghost" onClick={() => (setAsked(null), setText(''))}>
            Clear
          </Button>
        )}
      </form>
      {asked && notFound && <p className="mt-3 text-[14px] text-muted">No lot found for that {mode === 'code' ? 'code' : 'tag'}.</p>}
      {asked && q.isError && !notFound && (
        <Banner tone="error" title="Could not search" className="mt-3">
          {errorMessage(q.error)}
        </Banner>
      )}
      {asked && q.data && (
        <ul className="mt-3 flex flex-col gap-3">
          {q.data.map((l) => (
            <LookupResult key={l.id} lot={l} />
          ))}
        </ul>
      )}
    </section>
  )
}

function LookupResult({ lot }: { lot: LotLookup }) {
  const counts = Object.entries(lot.bagsByStatus)
  return (
    <li className="rounded-card border border-rule/70 px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <RefLink to={`/c/lots/${lot.id}`}>{lot.code}</RefLink>
        <Stream stream={lot.stream} />
        <Status status={lot.status} />
        {lot.hold !== 'NONE' && <Status status={lot.hold} />}
        <span className="text-[13px] text-muted">
          {lot.bagCount ?? '—'} bags · <Kg grams={lot.netG} /> at the gate
        </span>
        <RefLink to={`/c/lots/${lot.id}`} className="ml-auto font-sans">
          Custody timeline
        </RefLink>
      </div>
      {counts.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {counts.map(([s, n]) => (
            <Badge key={s} tone={s === 'MISSING' || s === 'VOID' ? 'danger' : 'neutral'}>
              {n} {humanize(s).toLowerCase()}
            </Badge>
          ))}
        </div>
      )}
      {lot.places.length === 0 ? (
        <p className="mt-1.5 text-[13.5px] text-muted">No bags in a store place or on a trip.</p>
      ) : (
        <ul className="mt-1.5 flex flex-col gap-0.5 text-[13.5px]">
          {lot.places.map((p, i) => (
            <li key={i} className="flex flex-wrap items-baseline gap-x-2">
              {p.placeKind === 'TRIP' && p.shipmentId ? (
                <span className="inline-flex items-center gap-1">
                  <Truck aria-hidden="true" className="text-info" />
                  <RefLink to={`/c/shipments/${p.shipmentId}`}>{p.shipmentCode ?? 'Trip'}</RefLink>
                </span>
              ) : (
                <span>{p.locationPath ?? '—'}</span>
              )}
              <span className="font-mono text-[12px] text-muted">
                {p.bags} bags{p.bagsOnHold > 0 && ` (${p.bagsOnHold} on hold)`} · <Kg grams={p.kgNowG} />
              </span>
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}

function SiteOnly({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: Array<{ value: string; label: string }> }) {
  return (
    <FilterBar onClear={() => onChange('')} active={value !== ''}>
      <SelectFilter label="Site" value={value} onChange={onChange} options={options} />
    </FilterBar>
  )
}

function RedTieList({ siteId, onSite, siteOptions }: { siteId: string; onSite: (v: string) => void; siteOptions: Array<{ value: string; label: string }> }) {
  const q = useRedTie(siteId || undefined)
  const columns: Column<RedTieRow>[] = [
    { id: 'tag', header: 'Bag tag', cell: (r) => <Code>{r.qrPayload}</Code>, sortValue: (r) => r.qrPayload },
    { id: 'lot', header: 'Lot', cell: (r) => <RefLink to={`/c/lots/${r.unitId}`}>{r.unitCode}</RefLink>, sortValue: (r) => r.unitCode },
    { id: 'place', header: 'Place', cell: (r) => r.locationPath ?? <span className="text-muted">Not in a store place</span>, sortValue: (r) => r.locationPath },
    { id: 'status', header: 'Bag', cell: (r) => <Status status={r.status} />, hideBelow: 'sm' },
  ]
  return (
    <DataTable<RedTieRow>
      caption="Bags of ordinary lots without a red tie"
      columns={columns}
      rows={q.data ?? []}
      getRowId={(r) => r.containerId}
      loading={q.isLoading}
      error={q.isError ? errorMessage(q.error) : undefined}
      searchText={(r) => `${r.qrPayload ?? ''} ${r.unitCode} ${r.locationPath ?? ''}`}
      searchPlaceholder="Tag, lot or place"
      filters={<SiteOnly value={siteId} onChange={onSite} options={siteOptions} />}
      empty={<EmptyState icon={<Tag />} title="Every ordinary bag has its red tie" description="Bags of a lot moved to ordinary show here until the red tie is scanned on." />}
    />
  )
}

function DockList({ siteId, onSite, siteOptions }: { siteId: string; onSite: (v: string) => void; siteOptions: Array<{ value: string; label: string }> }) {
  const q = useDock(siteId || undefined)
  const rows = q.data ?? []
  const stale = rows.filter((r) => isDockStale(r.since)).length
  const columns: Column<DockRow>[] = [
    { id: 'site', header: 'Site', cell: (r) => <Code>{r.siteCode}</Code>, sortValue: (r) => r.siteCode, hideBelow: 'sm' },
    { id: 'lot', header: 'Lot', cell: (r) => <RefLink to={`/c/lots/${r.unitId}`}>{r.unitCode}</RefLink>, sortValue: (r) => r.unitCode },
    { id: 'stream', header: 'Stream', cell: (r) => <Stream stream={r.stream} />, hideBelow: 'md' },
    { id: 'bags', header: 'Bags', align: 'right', mono: true, cell: (r) => r.bags, sortValue: (r) => r.bags },
    {
      id: 'since',
      header: 'Waiting since',
      sortValue: (r) => r.since,
      cell: (r) =>
        isDockStale(r.since) ? (
          <span className="inline-flex items-center gap-1.5 text-danger">
            <When at={r.since} relative /> <Badge tone="danger">Over a day</Badge>
          </span>
        ) : (
          <When at={r.since} relative />
        ),
    },
  ]
  return (
    <>
      {stale > 0 && (
        <Banner tone="warn" title={`${stale} ${stale === 1 ? 'lot has' : 'lots have'} waited on the dock for over a day`}>
          Ask the store keeper to put these bags away into a declared bay.
        </Banner>
      )}
      <DataTable<DockRow>
        caption="Bags received and not put away"
        columns={columns}
        rows={rows}
        getRowId={(r) => `${r.siteId}:${r.unitId}:${r.stream}`}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        filters={<SiteOnly value={siteId} onChange={onSite} options={siteOptions} />}
        empty={<EmptyState icon={<Package />} title="Nothing waiting on the dock" description="Bags show here from unloading until they are put away." />}
      />
    </>
  )
}
